import { faDownload } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { formatTransactionMethodLabel } from '@supply-mover-2-0/core';
import type { PortalTransactionListItemDto } from '@supply-mover-2-0/shared';
import {
	displayExternalId,
	isPortalCreditTransaction,
} from '@supply-mover-2-0/shared';
import { useTranslation } from '@supply-mover-2-0/web-ui';
import type { JSX } from 'react';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePortalAuth } from '../hooks/usePortalAuth';
import { saveOrShareBlob } from '../lib/downloadFile';
import { formatCurrency } from '../lib/formatCurrency';
import { formatStatementDate } from '../lib/formatStatementDate';
import * as portalTransactionService from '../services/portalTransactionService';

/** Status pill colors — keyed by the customer-facing display status. */
function statusClasses(status: string | null): string {
	switch ((status ?? '').toLowerCase()) {
		case 'succeeded':
		case 'completed':
		case 'settled':
			return 'bg-success-badge text-success-tint-fg border border-success-tint';
		case 'failed':
		case 'error':
			return 'bg-danger-badge text-danger-tint-fg border border-danger-tint';
		case 'processing':
		case 'pending':
			return 'bg-warning-badge text-warning-tint-fg border border-warning-tint';
		default:
			// Voided / Refunded / unknown — neutral terminal states
			return 'bg-sunken text-muted';
	}
}

/**
 * "Visa ****4242" / "Card ****4444" / "ACH ****6789" / "Cash" — whatever
 * describes it best. PD-16381: without a network name a card is named by its
 * funding ("Card" when unclassified), never by the stored 'Credit Card' type,
 * and it keeps its last four like the detail page and receipt do.
 */
function methodLabel(txn: PortalTransactionListItemDto): string {
	return formatTransactionMethodLabel({
		subtype: txn.mPaymentPaymentMethodSubtype,
		type: txn.mPaymentPaymentMethodType,
		cardCategory: txn.paymentMethodCardCategory,
		lastFour: txn.mPaymentPaymentMethodLastFour,
	});
}

/**
 * One transaction row (PD-14080) — shared by the account page's Recent
 * Transactions card and the full Transactions page so the two always agree.
 *
 * PD-14899: the row reads like a bank-statement line. The title is the
 * payment method (not the gateway transaction type), the status is the
 * capture-based `displayStatus` (falling back to the raw status against an
 * older API), and refunds render as negative amounts.
 *
 * PD-15870: a succeeded payment carries a Download Receipt action, every row
 * links to its detail page, and a row the login sees only through its own
 * documents (a child billed through a parent) says who paid.
 */
export function TransactionRow({
	txn,
	showClient,
}: {
	txn: PortalTransactionListItemDto;
	showClient: boolean;
}): JSX.Element {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { token } = usePortalAuth();
	const [isDownloading, setIsDownloading] = useState(false);

	const status = txn.displayStatus ?? txn.mPaymentStatus;
	const isCredit = isPortalCreditTransaction(txn.mPaymentType);
	// Decimal columns arrive as strings — normalize before signing.
	const amount = parseFloat(String(txn.mPaymentAmount ?? 0));
	// PD-15245 — linked invoices with their customer-facing number. Older
	// servers omit `relatedInvoices`; derive an id-only fallback from the
	// deprecated list so the links still render.
	const relatedInvoices =
		txn.relatedInvoices ??
		txn.relatedInvoiceIds.map((id) => ({ id, externalId: null }));
	const receiptAvailable = txn.receiptAvailable === true;
	const paidBy = txn.paidByAccountName ?? '';

	const handleDownload = useCallback(async () => {
		if (token == null) {
			return;
		}
		setIsDownloading(true);
		try {
			const blob = await portalTransactionService.downloadReceiptPdf(
				token,
				txn.id,
			);
			// Browser: normal download. Native shell: write to cache + share
			// sheet — the anchor click silently no-ops in the WebView
			// (PD-14606).
			await saveOrShareBlob(blob, `receipt-${txn.id}.pdf`);
		} catch (err) {
			console.error('Error downloading receipt PDF:', err);
		} finally {
			setIsDownloading(false);
		}
	}, [token, txn.id]);

	return (
		<div className="bg-brand-surface border border-brand-border rounded-xl p-3 flex items-start justify-between gap-3">
			<div className="min-w-0">
				<div className="flex items-center gap-2 flex-wrap">
					<button
						type="button"
						onClick={() => {
							void navigate(`/transactions/${txn.id}`);
						}}
						className="text-sm font-medium text-default hover:text-portal-accent hover:underline text-left"
					>
						{t(methodLabel(txn))}
					</button>
					<span
						className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusClasses(status)}`}
					>
						{t(status ?? '-')}
					</span>
				</div>
				{/* One meta line — related records sit inline next to the
				    date so the card matches the height of the order /
				    invoice / statement tiles (PD-14080 review feedback). */}
				<div className="text-xs text-brand-muted mt-0.5">
					{formatStatementDate(txn.mPaymentDatetime as unknown as string)}
					{showClient && txn.clientName !== '' && (
						<>
							{' · '}
							{t(txn.clientName)}
						</>
					)}
					{/* PD-15870 — the parent's card paid this account's invoices;
					    the amount shown is this account's portion. */}
					{paidBy !== '' && (
						<>
							{' · '}
							{t('Paid by {{account}}', { account: t(paidBy) })}
						</>
					)}
					{/* PD-15245 — show the invoice NUMBER, the same number the
					    invoices list shows, not the internal id. PD-16990 — and
					    not the ERP's own id where the invoice has a
					    customer-facing one (`displayExternalId`). */}
					{relatedInvoices.map((invoice, index) => (
						<span key={invoice.id}>
							{index === 0 ? ' · ' : ', '}
							<button
								type="button"
								onClick={() => {
									void navigate(`/invoices/${invoice.id}`);
								}}
								className="text-portal-accent hover:underline"
							>
								{index === 0 ? `${t('Invoice')} ` : ''}#
								{t(displayExternalId(invoice) ?? '')}
							</button>
						</span>
					))}
					{/* PD-15098: checkout payments link to orders, not invoices —
					    name the order the money paid for, mirroring the invoice
					    links above. */}
					{(txn.relatedOrderIds ?? []).map((orderId, index) => (
						<span key={orderId}>
							{index === 0 ? ' · ' : ', '}
							<button
								type="button"
								onClick={() => {
									void navigate(`/orders/${orderId}`);
								}}
								className="text-portal-accent hover:underline"
							>
								{index === 0 ? `${t('Order')} ` : ''}#{orderId}
							</button>
						</span>
					))}
					{/* PD-13704: a successful payment with no invoice links is money
					    put on the customer's account — describe it instead of leaving
					    the row bare. PD-15098: only when it has no ORDER links either;
					    "Payment on account" properly means an unallocated deposit, and
					    an order payment labeled that way misreads as one. */}
					{relatedInvoices.length === 0 &&
						(txn.relatedOrderIds ?? []).length === 0 &&
						/capture|sale|payment/i.test(txn.mPaymentType ?? '') &&
						/succe/i.test(status ?? '') && (
							<span>
								{' · '}
								{t('Payment on account')}
							</span>
						)}
				</div>
			</div>
			<div className="text-right shrink-0">
				<div className="text-sm font-semibold text-default">
					{/* PD-14899: refunds are money back — show them as credits. */}
					{formatCurrency(isCredit ? -Math.abs(amount) : amount)}
				</div>
				{/* PD-15870 — every succeeded payment has a receipt. */}
				{receiptAvailable && (
					<button
						type="button"
						onClick={() => {
							void handleDownload();
						}}
						disabled={isDownloading}
						className="mt-1 text-xs text-portal-accent hover:underline disabled:opacity-60"
					>
						<FontAwesomeIcon icon={faDownload} className="mr-1" />
						{isDownloading ? t('Preparing...') : t('Download Receipt')}
					</button>
				)}
			</div>
		</div>
	);
}
